export const fmtHoras = (seg) => {
  const h = Math.floor(seg / 3600);
  const m = Math.floor((seg % 3600) / 60);
  return `${h}h ${String(m).padStart(2, "0")}m`;
};

/** Duración corta para ausencias por tramo (incluye segundos si dura menos de 1 min). */
export const fmtDuracion = (seg) => {
  const n = Number(seg);
  if (!Number.isFinite(n) || n <= 0) return null;
  if (n < 60) return `${Math.round(n)} s`;
  const h = Math.floor(n / 3600);
  const m = Math.round((n % 3600) / 60);
  if (h <= 0) return `${m} min`;
  return m ? `${h} h ${m} min` : `${h} h`;
};

/** Zona usada para jornada y picados (misma que el backend). */
export const TZ_JORNADA = "Europe/Madrid";

export const offsetDesdeHoraServidor = (iso) => {
  if (!iso) return 0;
  const serverMs = Date.parse(iso);
  if (Number.isNaN(serverMs)) return 0;
  return serverMs - Date.now();
};

export const ahoraConOffset = (offsetMs = 0) => new Date(Date.now() + (offsetMs || 0));

export const fmtHoraReloj = (offsetMs = 0) =>
  ahoraConOffset(offsetMs).toLocaleTimeString("es-ES", {
    timeZone: TZ_JORNADA,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });

export const fmtFechaLargaReloj = (offsetMs = 0) =>
  ahoraConOffset(offsetMs).toLocaleDateString("es-ES", {
    timeZone: TZ_JORNADA,
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

export const fmtHora = (iso) =>
  new Date(iso).toLocaleTimeString("es-ES", {
    timeZone: TZ_JORNADA,
    hour: "2-digit",
    minute: "2-digit",
  });

export const fmtFecha = (iso) =>
  new Date(iso).toLocaleDateString("es-ES", {
    timeZone: TZ_JORNADA,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

export const fmtMesCorto = (iso) =>
  new Date(`${iso}T12:00:00`)
    .toLocaleDateString("es-ES", { month: "short" })
    .replace(".", "")
    .toUpperCase();

export const fmtDiaNum = (iso) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString("es-ES", { day: "numeric" });

export const hoyISO = () => new Date().toLocaleDateString("sv");

/** Une calle + CP + población + provincia para mostrar o geocodificar. */
export const fmtDireccionCompleta = (obj) => {
  if (!obj) return "";
  if (obj.direccion_completa) return obj.direccion_completa;
  const partes = [];
  if ((obj.direccion || "").trim()) partes.push(obj.direccion.trim());
  const loc = [obj.codigo_postal, obj.poblacion, obj.provincia].filter((x) => x && String(x).trim()).join(" ");
  if (loc.trim()) partes.push(loc.trim());
  return partes.join(", ");
};

export const inicioMesISO = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toLocaleDateString("sv");
};

export const finMesISO = (isoBase) => {
  const d = isoBase ? new Date(`${isoBase}T12:00:00`) : new Date();
  return new Date(d.getFullYear(), d.getMonth() + 1, 0).toLocaleDateString("sv");
};

export const ROL_LABEL = {
  super_admin: "Súper Admin",
  admin: "Administrador",
  manager: "Encargado",
  worker: "Trabajador",
};

export const TIPO_SOLICITUD = {
  vacaciones: "Vacaciones",
  asuntos_propios: "Asuntos propios",
  ausencia: "Asuntos propios",
  baja: "Baja",
  fichaje_manual: "Fichaje manual",
  fuera_horario: "Fuera de horario",
  hora_extra: "Autorización de horas extra",
  modificacion_fichaje: "Modificación de fichaje",
  justificacion_incidencia: "Justificación de incidencia",
  ausencia_justificada: "Ausencia justificada",
  cambio_jornada: "Cambio de jornada",
};

export const DIAS_VACACIONES_POR_MODO = { naturales: 30, habiles: 22 };

export const diasPorModoVacaciones = (modo) => DIAS_VACACIONES_POR_MODO[modo] ?? 30;

export function subtituloVacaciones({ dias_anuales, meses_trabajados, proporcional, modo_label }) {
  const partes = [modo_label];
  if (dias_anuales != null) partes.push(`${dias_anuales} d/año`);
  if (proporcional && meses_trabajados != null) {
    partes.push(`${meses_trabajados} meses (prorrateo)`);
  }
  return partes.join(" · ");
}

export const ESTADO_INTERCAMBIO = {
  pendiente_pareja: "Pendiente compañero",
  pendiente_encargado: "Pendiente encargado",
  aprobada: "Aprobada",
  rechazada: "Rechazada por encargado",
  rechazada_pareja: "Rechazada por compañero",
};

export const ESTADO_INTERCAMBIO_BADGE = {
  pendiente_pareja: "border-violet-200 bg-violet-100 text-violet-800",
  pendiente_encargado: "border-amber-200 bg-amber-100 text-amber-800",
  aprobada: "border-green-200 bg-green-100 text-green-800",
  rechazada: "border-red-200 bg-red-100 text-red-800",
  rechazada_pareja: "border-orange-200 bg-orange-100 text-orange-800",
};

export const ESTADO_SOLICITUD = {
  pendiente: "Pendiente",
  pendiente_confirmacion: "Pendiente tu confirmación",
  pendiente_trabajador: "Pendiente tu respuesta",
  aprobada: "Aprobada",
  aprobada_parcial: "Aprobada parcialmente",
  rechazada: "Rechazada",
  denegada_trabajador: "Rechazada por trabajador",
};

/** Etiqueta de estado. vista: "trabajador" | "revisor" (admin/encargado). */
export function etiquetaEstadoSolicitud(s, { vista = "trabajador" } = {}) {
  if (s?.rechazo_automatico) return "Rechazada (plazo vencido)";
  const estado = s?.estado;
  if (vista === "revisor") {
    const map = {
      pendiente: "Pendiente del encargado",
      pendiente_confirmacion: "Pendiente del trabajador",
      pendiente_trabajador: "Pendiente del trabajador",
      aprobada: "Aprobada",
      aprobada_parcial: "Aprobada parcialmente",
      rechazada: "Rechazada",
      denegada_trabajador: "Rechazada por el trabajador",
    };
    return map[estado] || ESTADO_SOLICITUD[estado] || estado;
  }
  return ESTADO_SOLICITUD[estado] || estado;
}

export const ESTADO_BADGE = {
  pendiente: "border-amber-200 bg-amber-100 text-amber-800",
  pendiente_confirmacion: "border-indigo-200 bg-indigo-100 text-indigo-800",
  pendiente_trabajador: "border-orange-200 bg-orange-100 text-orange-900",
  aprobada: "border-green-200 bg-green-100 text-green-800",
  aprobada_parcial: "border-teal-200 bg-teal-100 text-teal-800",
  rechazada: "border-red-200 bg-red-100 text-red-800",
  denegada_trabajador: "border-orange-200 bg-orange-100 text-orange-800",
};

export const MESES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

export const MESES_CORTOS = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
